import React, { useState, useEffect } from 'react';
import { NavigationMenu } from '../../components/NavigationMenu';
import { Button } from '../../components/Button';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { useGeneration } from '../../context/GenerationContext';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { Instagram, Link, HelpCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { tasksApi, reelsApi } from '../../services/api';
import { localStorageService } from '../../services/localStorageService';
import { PageHeader } from '../../components/PageHeader';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';
import { GenerationLimitsStatus } from '../../components/GenerationLimitsStatus';

// Компонент для отдельного поля ввода URL
interface UrlInputFieldProps {
  url: string;
  index: number;
  isValid: boolean;
  onChange: (url: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}

// Компонент для отдельного поля ввода аккаунта
interface AccountInputFieldProps {
  account: string;
  index: number;
  isValid: boolean;
  onChange: (account: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  isValidInstagramUrl: (url: string) => boolean;
  extractUsernameFromUrl: (url: string) => string | null;
}

const UrlInputField: React.FC<UrlInputFieldProps> = ({
  url,
  index,
  isValid,
  onChange,
  onRemove,
  canRemove
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  
  // Автоматически переходим в режим редактирования для пустых полей
  React.useEffect(() => {
    if (!url.trim()) {
      setIsEditing(true);
    }
  }, [url]);

  if (isValid && !isEditing) {
    // Валидная ссылка отображается как блок с синим фоном
    return (
      <div className="flex gap-2 items-center">
        <div 
          className="flex-1 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          onClick={() => setIsEditing(true)}
        >
          <div className="text-blue-700 dark:text-blue-300 text-sm font-medium truncate">
            {url}
          </div>
        </div>
        
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
            title="Удалить ссылку"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // Поле ввода для новых или редактируемых ссылок
  return (
    <div className="flex gap-2 items-center">
      <input
        type="text"
        value={url}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          // Выходим из режима редактирования только если есть валидная ссылка
          if (isValid) {
            setIsEditing(false);
          }
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && isValid) {
            setIsEditing(false);
          }
        }}
        placeholder={`https://www.instagram.com/reel/... ${index + 1}`}
        className="flex-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        autoFocus={isEditing}
      />
      
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Удалить ссылку"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

const AccountInputField: React.FC<AccountInputFieldProps> = ({
  account,
  index,
  isValid,
  onChange,
  onRemove,
  canRemove,
  isValidInstagramUrl,
  extractUsernameFromUrl
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [showInputError, setShowInputError] = React.useState(false);
  const [inputErrorTimeout, setInputErrorTimeout] = React.useState<NodeJS.Timeout | null>(null);
  
  // Автоматически переходим в режим редактирования для пустых полей
  React.useEffect(() => {
    if (!account.trim()) {
      setIsEditing(true);
    }
  }, [account]);

  // Очищаем таймер при размонтировании
  React.useEffect(() => {
    return () => {
      if (inputErrorTimeout) {
        clearTimeout(inputErrorTimeout);
      }
    };
  }, [inputErrorTimeout]);

  const handleInputChange = (originalValue: string) => {
    // Проверяем, если вставлен URL Instagram
    if (originalValue.includes('instagram.com')) {
      // Для URL проверяем валидность и извлекаем username
      if (isValidInstagramUrl(originalValue)) {
        const username = extractUsernameFromUrl(originalValue);
        if (username) {
          onChange(username); // Сохраняем username, а не URL
          return;
        }
      }
      // Если URL невалидный, показываем как есть, но isValid будет false
      onChange(originalValue);
      return;
    }
    
    // Обычная обработка для username без URL
    const filteredValue = originalValue.replace(/[^a-zA-Z0-9._@]/g, '');
    
    // Если были отфильтрованы символы, показываем ошибку
    if (originalValue !== filteredValue && !originalValue.includes('instagram.com')) {
      // Отменяем предыдущий таймер, если он есть
      if (inputErrorTimeout) {
        clearTimeout(inputErrorTimeout);
      }
      
      // Показываем сообщение об ошибке
      setShowInputError(true);
      
      // Устанавливаем таймер для скрытия сообщения
      const timeoutId = setTimeout(() => {
        setShowInputError(false);
      }, 3000);
      
      setInputErrorTimeout(timeoutId);
    }
    
    // Устанавливаем только допустимые символы
    onChange(filteredValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && account.trim()) {
      e.preventDefault();
      
      if (account.includes('instagram.com')) {
        // Обработка URL
        if (isValidInstagramUrl(account)) {
          const username = extractUsernameFromUrl(account);
          if (username) {
            onChange(username);
            setIsEditing(false);
          }
        }
      } else if (isValid) {
        setIsEditing(false);
      }
    }
  };

  const handleBlur = () => {
    // При потере фокуса обрабатываем URL или выходим из режима редактирования
    if (account.trim()) {
      if (account.includes('instagram.com')) {
        if (isValidInstagramUrl(account)) {
          const username = extractUsernameFromUrl(account);
          if (username) {
            onChange(username);
          }
        }
      }
      
      if (isValid) {
        setIsEditing(false);
      }
    }
  };

  if (isValid && !isEditing) {
    // Валидный аккаунт отображается как блок с синим фоном
    return (
      <div className="flex gap-2 items-center">
        <div 
          className="flex-1 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          onClick={() => setIsEditing(true)}
        >
          <div className="text-blue-700 dark:text-blue-300 text-sm font-medium truncate">
            @{account}
          </div>
        </div>
        
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
            title="Удалить аккаунт"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // Поле ввода для новых или редактируемых аккаунтов
  return (
    <div className="flex gap-2 items-center relative">
      {showInputError && (
        <div className="absolute -top-14 sm:-top-10 left-0 right-0 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 p-2 rounded-md text-xs border border-amber-200 dark:border-amber-800 shadow-sm z-10 text-center">
          Допустимы только латинские буквы, цифры, точки и подчеркивания
        </div>
      )}
      
      <input
        type="text"
        value={account}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={`@username или ссылка на аккаунт ${index + 1}`}
        className="flex-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        autoFocus={isEditing}
      />
      
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Удалить аккаунт"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

type TabType = 'accounts' | 'single';

// Интерфейс для типизации черновика
interface ReelsTranscriptionDraft {
  activeTab: TabType;
  usernames: string[]; // Оставляем для обратной совместимости
  accounts: string[]; // Новое поле для аккаунтов
  minViews: number;
  minViewsInput: string;
  isSmartAnalysis: boolean;
  periodNumber: number;
  periodUnit: 'days' | 'weeks';
  reelsUrl?: string; // Оставляем для обратной совместимости при загрузке старых черновиков
  reelsUrls: string[];
}

function ReelsTranscription() {
  const navigate = useCustomNavigate();
  const { token, hasActiveSubscription } = useAuth();
  const { canAddTask } = useGeneration();
  const [activeTab, setActiveTab] = useState<TabType>('accounts');
  
  const [usernames, setUsernames] = useState<string[]>([]); // Оставляем для обратной совместимости
  const [accounts, setAccounts] = useState<string[]>(['']); // Новый массив аккаунтов
  const [minViews, setMinViews] = useState<number>(1000);
  const [minViewsInput, setMinViewsInput] = useState<string>('1000');
  const [periodNumber, setPeriodNumber] = useState<number>(1);
  const [periodUnit, setPeriodUnit] = useState<'days' | 'weeks'>('weeks');
  const [isSmartAnalysis, setIsSmartAnalysis] = useState(false);
  const [showSmartFilterTooltip, setShowSmartFilterTooltip] = useState(false);
  
  // Функция для извлечения имени пользователя из URL Instagram
  const extractUsernameFromUrl = (url: string): string | null => {
    try {
      // Убираем лишние пробелы
      const cleanUrl = url.trim();
      
      // Регулярные выражения для разных форматов URL Instagram
      const patterns = [
        /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)\/?.*$/,
        /(?:https?:\/\/)?(?:www\.)?instagram\.com\/stories\/([a-zA-Z0-9._]+)\/?.*$/,
        /(?:https?:\/\/)?(?:www\.)?instagram\.com\/p\/[^\/]+\/\?taken-by=([a-zA-Z0-9._]+).*$/,
        /(?:https?:\/\/)?(?:www\.)?instagram\.com\/reel\/[^\/]+\/\?taken-by=([a-zA-Z0-9._]+).*$/
      ];
      
      for (const pattern of patterns) {
        const match = cleanUrl.match(pattern);
        if (match && match[1]) {
          const username = match[1];
          // Проверяем, что username не является служебным словом
          const excludedWords = ['p', 'reel', 'stories', 'tv', 'explore', 'accounts', 'help'];
          if (!excludedWords.includes(username.toLowerCase())) {
            return username;
          }
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };
  
    // Функция для валидации Instagram URL аккаунтов
  const isValidInstagramUrl = (url: string): boolean => {
    try {
      const cleanUrl = url.trim();
      const patterns = [
        /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?.*$/,
        /^https?:\/\/(www\.)?instagram\.com\/stories\/[a-zA-Z0-9._]+\/?.*$/,
        /^https?:\/\/(www\.)?instagram\.com\/p\/[^\/]+\/\?taken-by=[a-zA-Z0-9._]+.*$/,
        /^https?:\/\/(www\.)?instagram\.com\/reel\/[^\/]+\/\?taken-by=[a-zA-Z0-9._]+.*$/
      ];
      
      return patterns.some(pattern => pattern.test(cleanUrl));
    } catch (error) {
      return false;
    }
  };

  // Функция для валидации Instagram Reels URL
  const isValidReelsUrl = (url: string): boolean => {
    try {
      const cleanUrl = url.trim();
      const patterns = [
        /^https?:\/\/(www\.)?instagram\.com\/reel\/[a-zA-Z0-9._-]+\/?.*$/,
        /^https?:\/\/(www\.)?instagram\.com\/p\/[a-zA-Z0-9._-]+\/?.*$/
      ];
      
      return patterns.some(pattern => pattern.test(cleanUrl));
    } catch (error) {
    return false;
    }
  };

  // Функция для обработки ввода аккаунта (URL или username)
  const processAccountInput = (value: string): string | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    // Если это URL Instagram
    if (trimmedValue.includes('instagram.com/')) {
      // Сначала проверяем валидность URL
      if (!isValidInstagramUrl(trimmedValue)) return null;
      
      const username = extractUsernameFromUrl(trimmedValue);
      return username;
    }

    // Если это обычный username
    // Убираем @ если есть
    const cleanUsername = trimmedValue.startsWith('@') ? trimmedValue.slice(1) : trimmedValue;
    
    // Проверяем, что username содержит только допустимые символы
    if (/^[a-zA-Z0-9._]+$/.test(cleanUsername)) {
      return cleanUsername;
    }

    return null;
  };
  
  const [reelsUrls, setReelsUrls] = useState<string[]>(['']);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStartTime, setTaskStartTime] = useState<number | null>(null);
  const [taskStatusCheckCount, setTaskStatusCheckCount] = useState(0);
  const { status: generationStatus, loading: isLimitsLoading, error: limitsError } = useGenerationStatus('ReelsAnalysis');

  // Загрузка черновика при монтировании компонента
  useEffect(() => {
    // Используем единственный вызов для получения черновика
    const savedDraft = localStorageService.getReelsTranscriptionDraft<ReelsTranscriptionDraft>();
    if (savedDraft) {
      // Устанавливаем все состояния одновременно в одной операции, чтобы избежать лишних ререндеров
      if (savedDraft.activeTab) {
        // Проверяем, что activeTab является валидным TabType
        if (savedDraft.activeTab === 'accounts' || savedDraft.activeTab === 'single') {
          setActiveTab(savedDraft.activeTab);
        }
      }
      
      // Загружаем аккаунты - приоритет у accounts, иначе из usernames для обратной совместимости
      if (savedDraft.accounts && savedDraft.accounts.length > 0) {
        setAccounts(savedDraft.accounts);
      } else if (savedDraft.usernames && savedDraft.usernames.length > 0) {
        // Конвертируем старые usernames в новый формат accounts
        setAccounts([...savedDraft.usernames, '']);
        setUsernames(savedDraft.usernames);
      }
      
      if (savedDraft.minViews !== undefined) setMinViews(savedDraft.minViews);
      if (savedDraft.minViewsInput) setMinViewsInput(savedDraft.minViewsInput);
      if (savedDraft.periodNumber) setPeriodNumber(savedDraft.periodNumber);
      if (savedDraft.periodUnit) setPeriodUnit(savedDraft.periodUnit);
      // Загружаем URLs - приоритет у reelsUrls, иначе из reelsUrl для обратной совместимости
      if (savedDraft.reelsUrls && savedDraft.reelsUrls.length > 0) {
        setReelsUrls(savedDraft.reelsUrls);
      } else if (savedDraft.reelsUrl) {
        setReelsUrls([savedDraft.reelsUrl]);
      }
      if (savedDraft.isSmartAnalysis) setIsSmartAnalysis(savedDraft.isSmartAnalysis);
    }
  }, []);

  // Сохранение черновика при изменении состояний
  useEffect(() => {
    // Используем debounce для сохранения черновика, чтобы не перегружать localStorage
    const saveTimeout = setTimeout(() => {
      const draftData: ReelsTranscriptionDraft = {
        activeTab,
        usernames, // Оставляем для обратной совместимости
        accounts, // Новое поле для аккаунтов
        minViews,
        minViewsInput,
        periodNumber,
        periodUnit,
        reelsUrls,
        isSmartAnalysis
      };
      
      localStorageService.saveReelsTranscriptionDraft(draftData);
    }, 500); // задержка в 500 мс для дебаунсинга
    
    return () => {
      clearTimeout(saveTimeout);
    };
  }, [activeTab, usernames, accounts, minViews, minViewsInput, periodNumber, periodUnit, reelsUrls, isSmartAnalysis]);

  // Закрытие тултипа Smart фильтра при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showSmartFilterTooltip && !target.closest('.smart-filter-tooltip-container')) {
        setShowSmartFilterTooltip(false);
      }
    };

    if (showSmartFilterTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showSmartFilterTooltip]);

  // Функция для очистки черновика после успешного запуска задачи
  const clearReelsTranscriptionDraft = () => {
    localStorageService.clearReelsTranscriptionDraft();
  };


  const handleMinViewsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Разрешаем только цифры или пустую строку
    if (value === '' || /^\d*$/.test(value)) {
      setMinViewsInput(value);
      
      // Преобразуем в число для использования в API
      const number = value === '' ? 0 : parseInt(value);
      setMinViews(number);
    }
  };

  const handlePeriodNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setPeriodNumber(1);
      return;
    }
    
    const number = parseInt(value);
    if (!isNaN(number) && number >= 1 && number <= 52) {
      setPeriodNumber(number);
    }
  };

  // Функция для проверки статуса задачи
  useEffect(() => {
    if (!currentTaskId) return;

    // Не проверяем статус временных задач
    if (currentTaskId.startsWith('temp-')) {
      console.log(`[Debug] Пропускаем проверку статуса для временного ID: ${currentTaskId}`);
      return;
    }

    // Устанавливаем время начала задачи
    if (!taskStartTime) {
      setTaskStartTime(Date.now());
    }

    const checkTaskStatus = async () => {
      try {
        // Увеличиваем счетчик проверок
        setTaskStatusCheckCount(prev => prev + 1);
        
        // Проверяем, не слишком ли долго выполняется задача (более 15 минут)
        if (taskStartTime && (Date.now() - taskStartTime > 15 * 60 * 1000)) {
          console.log(`[Debug] Задача ${currentTaskId} выполняется более 15 минут. Перенаправляем на главную страницу.`);
          // Не показываем ошибку, просто перенаправляем на главную
          navigate('/');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
          return;
        }
        
        // Используем метод tasksApi.getTaskResult для проверки статуса
        const response = await tasksApi.getTaskResult(currentTaskId);
        const task = response.data;
        
        // Проверяем, завершена ли задача
        if (task.status === 'Successfull' || task.status === 'Successful') {
          // Дополнительно проверяем наличие данных результата
          if (task.data) {
            // Перенаправляем на страницу задач, так как страницы результатов удалены
            let redirectUrl = '/tasks';
            
            // Очищаем текущую задачу перед перенаправлением
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
            
            // Добавляем задержку перед перенаправлением, чтобы данные точно успели обработаться
            setTimeout(() => {
              try {
                // Сохраняем ID задачи в sessionStorage для гарантированного доступа на странице результатов
                sessionStorage.setItem('lastReelsTaskId', currentTaskId || '');
                
                // Используем navigate вместо прямого изменения window.location.href
                navigate(redirectUrl, { 
                  state: { 
                    taskId: currentTaskId, 
                    type: 'reels' 
                  }, 
                  replace: true 
                });
              } catch (err) {
                console.error('Ошибка при перенаправлении:', err);
                
                // Запасной вариант - также используем navigate
                navigate(redirectUrl, { 
                  state: { 
                    taskId: currentTaskId, 
                    type: 'reels' 
                  } 
                });
              }
            }, 500);
          } else {
            console.error('Задача завершена, но данные пустые');
            
            // Очищаем текущую задачу
            setCurrentTaskId(null);
            setTaskStartTime(null);
            setTaskStatusCheckCount(0);
            setIsLoading(false);
          }
        } else if (task.status === 'Error' || task.status === 'Cancelled') {
          // Если ошибка или отмена, тоже очищаем задачу
          
          if (task.status === 'Error') {
          }
          
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Ошибка при проверке статуса задачи:', error);
        
        // Если это 404 ошибка (задача не найдена), возможно задача еще создается
        if (error && typeof error === 'object' && 'response' in error) {
          const response = (error as any).response;
          if (response?.status === 404) {
            console.log(`[Debug] Задача ${currentTaskId} еще не найдена на сервере, продолжаем ожидание...`);
            return; // Продолжаем polling
          }
        }
        
        // Для других ошибок - прекращаем отслеживание после нескольких попыток
        if (taskStatusCheckCount > 10) {
          console.log('[Debug] Слишком много ошибок при проверке статуса. Прекращаем отслеживание.');
          setCurrentTaskId(null);
          setTaskStartTime(null);
          setTaskStatusCheckCount(0);
          setIsLoading(false);
        }
      }
    };

    // Проверяем статус каждые 3 секунды для единообразия
    const intervalId = setInterval(checkTaskStatus, 3000);

    // Очищаем интервал при размонтировании компонента
    return () => {
      clearInterval(intervalId);
    };
  }, [currentTaskId, navigate, taskStartTime, taskStatusCheckCount]);

  const handleSubmitAccounts = async () => {
    if (!token) {
      navigate('/auth/login');
      return;
    }

    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    // Получаем список валидных аккаунтов
    const validAccounts = accounts
      .map(account => processAccountInput(account))
      .filter((account): account is string => account !== null);

    if (validAccounts.length === 0) {
      return;
    }

    if (!canAddTask) {
      return;
    }

    // Показываем анимацию сразу
    setIsLoading(true);
    
    // Очищаем черновик сразу
    clearReelsTranscriptionDraft();

    try {
      console.log('[Debug REELS API] Отправка запроса на анализ рилсов через reelsApi');
      console.log('[Debug REELS API] Данные запроса:', {
        instagramAccounts: validAccounts,
        minimumViews: minViews,
        isSmartAnalysis: isSmartAnalysis,
        period: { number: periodNumber, unit: periodUnit }
      });
      
      // Отправляем запрос через reelsApi (не ждем ответа)
      reelsApi.analyzeAccounts({
          instagramAccounts: validAccounts,
          minimumViews: minViews,
        isSmartAnalysis: isSmartAnalysis,
          period: {
            number: periodNumber,
            unit: periodUnit
          }
      }).then((response) => {
        const data = response.data;
        console.log('[Debug REELS API] Полный ответ от сервера:', data);
        
        // Поиск идентификатора задачи в ответе
        const realTaskId = data.taskId || data.id || (data.task && data.task.id);
        
        if (realTaskId) {
          console.log(`[Debug REELS API] Получен реальный taskId: ${realTaskId}`);
          // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
          // startGeneration убран, чтобы избежать дублирования
        }
      }).catch((error) => {
        console.error('[Debug REELS API] Error analyzing reels:', error);
        console.error('[Debug REELS API] Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error: any) {
      console.error('Error starting accounts analysis:', error);
      setIsLoading(false);
    }
  };

  const handleSubmitSingleReels = async () => {
    if (!token) {
      navigate('/auth/login');
      return;
    }

    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    // Получаем список валидных URL
    const validUrls = reelsUrls.filter(url => url.trim() && isValidReelsUrl(url));
    
    // Проверяем, что есть валидные URL для анализа
    if (validUrls.length === 0) {
      return;
    }

    if (!canAddTask) {
      return;
    }

    // Показываем анимацию сразу
    setIsLoading(true);
    
    // Очищаем черновик сразу
    clearReelsTranscriptionDraft();

    try {
      console.log('[Debug] Отправка запроса на анализ рилсов', {
        urls: validUrls,
        count: validUrls.length
      });
      
      reelsApi.analyzeByUrls({
          urls: validUrls
      }).then((response) => {
        const data = response.data;
        console.log('[Debug] Полный ответ от сервера:', data);
        
        // Поиск идентификатора задачи в ответе
        const realTaskId = data.taskId || data.id || (data.task && data.task.id);
        
        if (realTaskId) {
          console.log(`[Debug] Получен реальный taskId: ${realTaskId}`);
          // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
        }
      }).catch((error) => {
        console.error('Error analyzing reels:', error);
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error: any) {
      console.error('Error starting reels analysis:', error);
      setIsLoading(false);
    }
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="hidden md:block">
        <NavigationMenu />
        </div>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-96px)]">
          <div className="scale-125 transform">
            <LoadingAnimation size="large" text="Анализ Reels..." />
          </div>
          <div className="mt-16 max-w-2xl text-center">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
              Анализ Reels может занять некоторое время.
              <br /><br />
              Ваша задача обрабатывается в фоновом режиме. Вы можете перейти на другую страницу, а результаты будут доступны в разделе "Задачи".
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      <div className="hidden md:block">
      <NavigationMenu />
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Анализ Reels"
          subtitle="Выберите формат анализа Instagram Reels для получения детальной информации"
        />

        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-4 sm:p-6 md:p-8 overflow-hidden">
            <div className="flex justify-between mb-8 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('accounts')}
                className={`py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm border-b-2 transition-colors flex-1 ${
                  activeTab === 'accounts'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center justify-center sm:justify-start">
                  <Instagram className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  По аккаунтам
                </div>
              </button>
              <button
                onClick={() => setActiveTab('single')}
                className={`py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm border-b-2 transition-colors flex-1 ${
                  activeTab === 'single'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center justify-center sm:justify-start">
                  <Link className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  По ссылкам
                </div>
              </button>
            </div>
            
            {activeTab === 'accounts' ? (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Введите Instagram аккаунты для анализа их Reels
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Instagram аккаунты (до 10)
                  </label>
                  
                  <div className="space-y-3">
                    {accounts.map((account, index) => {
                      const processedAccount = processAccountInput(account);
                      const isValidAccount = Boolean(processedAccount);
                      
                      return (
                        <AccountInputField
                          key={index}
                          account={account}
                          index={index}
                          isValid={isValidAccount}
                          onChange={(newAccount) => {
                            const newAccounts = [...accounts];
                            newAccounts[index] = newAccount;
                            setAccounts(newAccounts);
                          }}
                          onRemove={() => {
                            const newAccounts = accounts.filter((_, i) => i !== index);
                            setAccounts(newAccounts);
                          }}
                          canRemove={accounts.length > 1}
                          isValidInstagramUrl={isValidInstagramUrl}
                          extractUsernameFromUrl={extractUsernameFromUrl}
                        />
                      );
                    })}
                  </div>
                  
                  {accounts.length < 10 && (
                    <button
                      type="button"
                      onClick={() => setAccounts([...accounts, ''])}
                      className="mt-3 flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Добавить аккаунт
                    </button>
                  )}
                  
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    Добавлено {accounts.filter(account => processAccountInput(account) !== null).length} из 10 аккаунтов
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Минимальное количество просмотров
                  </label>
                  <div className="flex items-center gap-4">
                  <input
                      type="text"
                      value={minViewsInput}
                    onChange={handleMinViewsChange}
                      disabled={isSmartAnalysis}
                      className="w-1/2 p-2 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-sm disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                    />
                    <label htmlFor="smart-analysis" className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          id="smart-analysis"
                          type="checkbox"
                          checked={isSmartAnalysis}
                          onChange={(e) => setIsSmartAnalysis(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 whitespace-nowrap">
                        Smart фильтр
                      </span>
                      <div className="relative group ml-2 smart-filter-tooltip-container">
                        <HelpCircle 
                          className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-pointer transition-colors touch-manipulation" 
                          aria-label="Условия Smart фильтра"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowSmartFilterTooltip(!showSmartFilterTooltip);
                          }}
                          onMouseEnter={() => {
                            // Только для десктопа
                            if (window.innerWidth >= 768) {
                              setShowSmartFilterTooltip(true);
                            }
                          }}
                          onMouseLeave={() => {
                            // Только для десктопа
                            if (window.innerWidth >= 768) {
                              setShowSmartFilterTooltip(false);
                            }
                          }}
                        />
                        <div 
                          className={`absolute right-0 md:left-1/2 md:-translate-x-1/2 bottom-full mb-2 w-72 max-w-[calc(100vw-1rem)] p-3 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg transition-all duration-200 z-[100] ${
                            showSmartFilterTooltip ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          onMouseEnter={() => {
                            if (window.innerWidth >= 768) {
                              setShowSmartFilterTooltip(true);
                            }
                          }}
                          onMouseLeave={() => {
                            if (window.innerWidth >= 768) {
                              setShowSmartFilterTooltip(false);
                            }
                          }}
                        >
                          <div className="font-semibold mb-2">Условия Smart фильтра:</div>
                          <ul className="space-y-1.5 text-gray-300">
                            <li>• Просмотры &gt; 20% от подписчиков</li>
                            <li>• Лайки &gt; 3% от просмотров</li>
                            <li>• Комментарии &gt; 0.3% от просмотров</li>
                            <li>• Длительность ≥ 15 сек</li>
                          </ul>
                          <div className="absolute right-4 md:left-1/2 md:-translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    За последние
                  </label>
                  <div className="flex gap-2 sm:gap-4 items-center">
                    <div className="flex items-center">
                      <Button 
                        onClick={() => setPeriodNumber(prev => Math.max(1, prev - 1))}
                        variant="primary"
                        className="h-10 sm:h-12 w-10 sm:w-12 flex items-center justify-center rounded-l-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-lg sm:text-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        -
                      </Button>
                      <input
                        type="text"
                        value={periodNumber || ''}
                        onChange={handlePeriodNumberChange}
                        className="h-10 sm:h-12 w-16 sm:w-20 p-0 text-center border-y border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-base sm:text-lg"
                      />
                      <Button 
                        onClick={() => setPeriodNumber(prev => Math.min(52, prev + 1))}
                        variant="primary"
                        className="h-10 sm:h-12 w-10 sm:w-12 flex items-center justify-center rounded-r-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-lg sm:text-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        +
                      </Button>
                    </div>
                    <select
                      value={periodUnit}
                      onChange={(e) => setPeriodUnit(e.target.value as 'days' | 'weeks')}
                      className="flex-1 p-2 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="days">дней</option>
                      <option value="weeks">недель</option>
                    </select>
                  </div>
                </div>

                <GenerationLimitsStatus 
                  className="mb-4" 
                  generationType="ReelsAnalysis" 
                  status={generationStatus}
                  loading={isLimitsLoading}
                  error={limitsError}
                />

                <Button
                  onClick={handleSubmitAccounts}
                  disabled={accounts.filter(account => processAccountInput(account) !== null).length === 0 || isLoading || !canAddTask || !generationStatus?.canGenerate}
                  variant="primary"
                  fullWidth
                >
                  {!canAddTask ? 'Достигнут лимит задач' : (isLoading ? 'Анализ...' : 'Начать анализ')}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Введите прямые ссылки на Instagram Reels для анализа
                  </p>
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ссылки на Instagram Reels (до 15 штук)
                  </label>
                  
                  <div className="space-y-3">
                    {reelsUrls.map((url, index) => {
                      const isValidUrl = Boolean(url.trim() && isValidReelsUrl(url));
                      
                      return (
                        <UrlInputField
                          key={index}
                          url={url}
                          index={index}
                          isValid={isValidUrl}
                          onChange={(newUrl) => {
                            const newUrls = [...reelsUrls];
                            newUrls[index] = newUrl;
                            setReelsUrls(newUrls);
                          }}
                          onRemove={() => {
                            const newUrls = reelsUrls.filter((_, i) => i !== index);
                            setReelsUrls(newUrls);
                          }}
                          canRemove={reelsUrls.length > 1}
                        />
                      );
                    })}
                  </div>
                  
                  {reelsUrls.length < 15 && (generationStatus?.unlimited || reelsUrls.length < (generationStatus?.remaining || 15)) && (
                    <button
                      type="button"
                      onClick={() => setReelsUrls([...reelsUrls, ''])}
                      className="mt-3 flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Добавить ссылку
                    </button>
                  )}
                  
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    Добавлено {reelsUrls.filter(url => url.trim() && isValidReelsUrl(url)).length} из {generationStatus?.unlimited ? 15 : Math.min(15, generationStatus?.remaining || 15)} ссылок
                  </div>
                </div>

                <GenerationLimitsStatus 
                  className="mb-4" 
                  generationType="ReelsAnalysis" 
                  status={generationStatus}
                  loading={isLimitsLoading}
                  error={limitsError}
                />

                <Button
                  onClick={handleSubmitSingleReels}
                  disabled={reelsUrls.filter(url => url.trim() && isValidReelsUrl(url)).length === 0 || isLoading || !canAddTask || !generationStatus?.canGenerate}
                  variant="primary"
                  fullWidth
                >
                  {!canAddTask ? 'Достигнут лимит задач' : (isLoading ? 'Анализ...' : `Анализировать`)}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
}

export default ReelsTranscription;